import React, { useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { IAppState } from "../../../store";
import { TopBar } from '../../TopBar/TopBar';
import { Box, Button, Dialog, Grid, Link, makeStyles, Typography } from '@material-ui/core';
import SendIcon from '@material-ui/icons/Send';
import PageModules from "../../PageModules";
import FeaturedOffers from "../../FeaturedOffers/FeaturedOffers";
import Authenticator from "../Auth";
import { BookingType, IIdentityConfiguration } from "../../../reducers/bookings/types";
import { useTranslation } from "react-i18next";
import styles from './index.module.css'
import { bookingCategories } from "./utils";
import { trackFunnelStep, FunnelStage, FunnelFlow } from "../../../utils/funnelTracking";
import { GET_IDV_CONFIGURATION } from "../../../graphql/user/getIdvConfigurationQuery";
import { useLazyQuery } from "@apollo/client";
import { Alert } from "@material-ui/lab";

const useStyles = makeStyles((theme) => ({
  productTab: {
    position: 'relative',
    backgroundColor: 'transparent',
    border: 'none',
    width: '100%',
    textAlign: 'center',
    padding: theme.spacing(1.25, 0),
    color: "#fff",
    fontWeight: 600,
    display: "block",
    "&:hover": {
      backgroundColor: "#fff",
      color: "#000",
      borderRadius: 12,
      '&:after': {
        content: '""',
        position: 'absolute',
        left: '50%',
        top: '100%',
        height: 16,
        width: 16,
        backgroundColor: '#fff',
        boxShadow: `2px 2px 4px 0 rgba(0, 0, 0, 0.16)`,
        borderRadius: 2,
        transform: 'translate(-50%, -50%) rotate(45deg)'
      }
    }
  },
  active: {
    boxShadow: `0 3px 6px 0 rgba(0, 0, 0, 0.16)`,
    backgroundColor: '#fff',
    borderRadius: 12,
    color: "#000",
    "&:hover": {
      backgroundColor: "#fff",
      color: "#000",
      borderRadius: 12
    },
    '&:after': {
      content: '""',
      position: 'absolute',
      left: '50%',
      top: '100%',
      height: 16,
      width: 16,
      backgroundColor: '#fff',
      boxShadow: `2px 2px 4px 0 rgba(0, 0, 0, 0.16)`,
      borderRadius: 2,
      transform: 'translate(-50%, -50%) rotate(45deg)'
    }
  },
  bookingTypeGridContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flexDirection: 'row',
    [theme.breakpoints.between('sm', 'md')]: {
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      marginTop: '207px',
    },
  }
}));

export const Landing: React.FC = () => {
  const classes = useStyles()
  const location = useLocation();
  const history = useHistory();
  const website = useSelector((state: IAppState) => state.consumerWebsiteReducer.consumerWebsite);
  const authState = useSelector((state: IAppState) => state.authReducer);
  const { identityVerification } = website.tenancy;

  const [bookingType, setBookingType] = useState<BookingType>(BookingType.RENTAL)
  const [authDialogOpen, setAuthDialogOpen] = useState<boolean>(false);
  const [idvConfiguration, setIdvConfiguration] = useState<IIdentityConfiguration | null>()

  const { t } = useTranslation();

  const isContractHire = website.organisation.bookingCategories.some((category) => category.isContractHire === true)

  const [loadIdvConfiguration, { loading: loadingIdvConfiguration, data: idvData }] = useLazyQuery(
    GET_IDV_CONFIGURATION,
    {
      fetchPolicy: "network-only",
      onCompleted: (data) => {
        if (data.getIdvConfiguration) {
          setIdvConfiguration(data.getIdvConfiguration)
        }
      },
      onError: (err) => {

      }
    }
  );

  const modifiedBookingCategories = Object.keys(bookingCategories).reduce(
    (acc, key) => {
      const type = key as BookingType;

      acc[type] = {
        ...bookingCategories[type],
        enabled: website?.bookingTypes?.includes(type),
        ...(type === BookingType.SUBSCRIPTION && {
          label: isContractHire ? "Contract Hire" : "Subscriptions",
          subLabel: isContractHire
            ? "Contract Hire vehicle for weekly/monthly"
            : "Subscribe vehicle for weekly/monthly",
          buttonText: isContractHire ? "Contract Now" : "Subscribe Now",
        })
      };

      return acc;
    },
    {} as typeof bookingCategories
  );

  useEffect(() => {
    if (website?.bookingTypes?.length && !bookingType) {
      const sorted = [...website.bookingTypes].sort();
      setBookingType(sorted[0] as BookingType);
    }
  }, [website, bookingType]);

  const onSignIn = () => {
    setAuthDialogOpen(false);
  }

  const handleBookNow = () => {
    // If multi-org and not authenticated, show auth dialog
    if (website?.isMultiOrganisation && !authState.user) {
      setAuthDialogOpen(true)
    } else {
      trackFunnelStep(
        website?.enableUserDropoffTrackingWebsite,
        (bookingType as unknown as FunnelFlow) || FunnelFlow.RENTAL,
        FunnelStage.LANDING
      );
      // Navigate to new-booking with selected booking type
      history.push(`/new-booking?bookingType=${bookingType}`);
    }
  }

  const handleLocationSelection = () => {
    // Handle location selection if needed
  }

  useEffect(() => {
    if (website?.bookingTypes?.length === 1) {
      trackFunnelStep(
        website?.enableUserDropoffTrackingWebsite,
        (website.bookingTypes[0] as unknown as FunnelFlow) || FunnelFlow.RENTAL,
        FunnelStage.LANDING
      );
      history.replace(`/new-booking?bookingType=${website.bookingTypes[0]}`);
    }
  }, [website?.bookingTypes, history]);


  useEffect(() => {
    if (identityVerification && authState.user) {
      loadIdvConfiguration({
        variables: {
          configurationId: identityVerification.id
        }
      })
    }
  }, [identityVerification, authState.user])

  const isVerificationRequired =
    identityVerification &&
    identityVerification?.enabled &&
    idvConfiguration?.verificationPoints.bookingCreation.enabled &&
    idvConfiguration?.verificationPoints?.bookingCreation?.required &&
    authState?.user?.customer?.identityVerification?.status !== "APPROVED"

  const logoSize = website.logoSize || 40
  const topBarHeight = logoSize > 50 ? logoSize + 20 : 70

  return (
    <>
      <TopBar
        height={topBarHeight}
        logoSize={logoSize}
      />
      <div className={styles.bookingTypeContainer}>
        <Grid container xs={12} className={classes.bookingTypeGridContainer}>
          <Grid item xs={12} md={6} sm={12} lg={6} container justify="center" alignItems="center" style={{ marginBottom: 10 }}>
            <Box className={styles.boxMainCon}>
              <Box className={styles.productTabLt}>
                {Object.keys(modifiedBookingCategories).map(category => {
                  const bookingCategory = modifiedBookingCategories[category as BookingType];
                  return bookingCategory.enabled &&
                    <Button
                      key={category}
                      className={`${classes.productTab} ${bookingType === category ? classes.active : ''}`}
                      onClick={() => {
                        setBookingType(category as BookingType)
                      }}
                    >
                      <Typography variant="h6" className={styles.productHeading}>
                        {t<string>(`misc:buttonKey.${bookingCategory.label}`)}
                      </Typography>
                    </Button>
                })}
              </Box>
              <Box m={2} mt={2} justifyItems={"center"}>
                <Typography variant="h3" className={styles.productSubHeading}>
                  {t<string>(`misc:subLabelKey.${modifiedBookingCategories[bookingType].subLabel}`)}
                </Typography>
              </Box>
              <Box className={styles.bookNowButtonBox}>
                <Button
                  className={styles.btnBookNow}
                  variant="contained"
                  color="primary"
                  disabled={isVerificationRequired}
                  endIcon={<SendIcon></SendIcon>}
                  onClick={handleBookNow}
                >
                  {t<string>(`misc:buttonTextKey.${modifiedBookingCategories[bookingType].buttonText}`)}
                </Button>
              </Box>
              {
                isVerificationRequired && (
                  <Box mt={1} mb={1}>
                    <Alert severity="error">
                      Identity verification has not been completed.{' '}
                      <Link
                        component="button"
                        type="button"
                        onClick={() => history.push(`/account`)}
                      >
                        Verify now
                      </Link>
                    </Alert>
                  </Box>
                )
              }
            </Box>
          </Grid>
          <Grid item xs={12} md={12} sm={12} lg={6}>
            <div className={styles.appBody}>
              <PageModules setLocation={handleLocationSelection} showCarousel={false} bookingType={bookingType as BookingType} />
            </div>
          </Grid>
        </Grid>
      </div>

      <FeaturedOffers bookingType={bookingType} />

      <Dialog open={authDialogOpen} onClose={() => setAuthDialogOpen(false)} maxWidth="md" >
        <div className={styles.authDialog}>
          <Authenticator onSignIn={onSignIn} />
        </div>
      </Dialog>
    </>
  );
};

export default Landing;
